import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';

import { RequestStatus } from '@telialt/models/src/RequestModel';

import { InputWithSuggestionsQueryKey } from '../constants';
import { Suggestion } from '../model';

type UseLoadSuggestionsReturn = {
    isLoading: boolean;
    suggestions: Suggestion[];
    submitKeyword: (keyword: string) => void;
};

export const useLoadSuggestions = (
    loadSuggestions: (keyword: string, signal: AbortSignal) => Promise<Suggestion[]>,
    searchKeyword: string,
    isEnabled: boolean,
    minKeywordLength: number,
    searchDebounceTime: number,
): UseLoadSuggestionsReturn => {
    const [isLoading, setIsLoading] = useState(false);
    const [submittedKeyword, setSubmittedKeyword] = useState('');
    const submittedKeywordRef = useRef(submittedKeyword);
    submittedKeywordRef.current = submittedKeyword;

    const clearSubmittedKeyword = useCallback(() => {
        setSubmittedKeyword('');
        setIsLoading(false);
    }, []);

    const submitKeyword = useCallback((keyword: string) => {
        setSubmittedKeyword(keyword);
    }, []);

    const debouncedSubmitKeyword = useMemo(
        () => debounce(submitKeyword, searchDebounceTime),
        [searchDebounceTime, submitKeyword],
    );

    useEffect(() => {
        if (!isEnabled || submittedKeywordRef.current === searchKeyword) {
            return;
        }
        if (searchKeyword.length >= minKeywordLength) {
            setIsLoading(true);
            debouncedSubmitKeyword(searchKeyword);
        } else {
            debouncedSubmitKeyword.cancel();
            clearSubmittedKeyword();
        }
    }, [searchKeyword, debouncedSubmitKeyword, setIsLoading, clearSubmittedKeyword, isEnabled, minKeywordLength]);

    useEffect(() => {
        return () => {
            debouncedSubmitKeyword.cancel();
        };
    }, [debouncedSubmitKeyword]);

    const { data, status, error, fetchStatus } = useQuery<Suggestion[]>({
        queryKey: [InputWithSuggestionsQueryKey.LOAD_SUGGESTIONS, submittedKeyword],
        queryFn: ({ signal }) => {
            return submittedKeyword ? loadSuggestions(submittedKeyword, signal) : [];
        },
    });

    useEffect(() => {
        if (error || data) {
            setIsLoading(false);
        }
    }, [data, error]);

    const isQueryLoading = status === RequestStatus.Pending && fetchStatus !== RequestStatus.Idle;

    return { isLoading: isLoading || isQueryLoading, suggestions: data || [], submitKeyword };
};
